import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env/environment";
import moment from "moment";
import { from, Observable, throwError, lastValueFrom } from "rxjs";
import { MaintenanceConfig } from "./maintenance-config.model";
import { MaintenanceService } from "./maintenance.service";

@Injectable({
  providedIn: 'root'
})
export class MaintenanceInterceptor implements HttpInterceptor {
  MAINTENANCE_ACTIVATED_ERROR = "MAINTENANCE_ACTIVATED_ERROR";

  maintenanceConfig: MaintenanceConfig;

  constructor(private maintenanceService: MaintenanceService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (environment.api.mobilityUrl) {
      return from(this.handleRequest(request, next));
    }
    return next.handle(request);
  }

  private async handleRequest(request: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
    const currentDate = moment(new Date());
    const browserUrl = window.location.href;

    // get new maintenanceConfig from config file
    this.maintenanceConfig = this.maintenanceService.getMaintenanceConfig();

    if (this.maintenanceConfig) {
      if (this.isMaintenanceActive(currentDate)) {
        this.maintenanceService.nextMaintenanceActivatedSubject(true);
        this.maintenanceService.nextShowBannerSubject(false);

        if (!browserUrl.includes('/maintenance')) {
          window.location.href = '/maintenance';
          return lastValueFrom(throwError(() => new Error(this.MAINTENANCE_ACTIVATED_ERROR)));
        }
      } else {
        // maintenance not enabled, check if show banner
        this.maintenanceService.nextMaintenanceActivatedSubject(false);
        this.maintenanceService.nextShowBannerSubject(this.isBannerActive(currentDate)); // Optimized this line

        if (browserUrl.includes('/maintenance')) {
          // redirect to home page
          window.location.href = '';
        }
      }
    }

    if (this.maintenanceService.getMaintenanceActivatedSubject() && !browserUrl.includes('/maintenance')) {
      return lastValueFrom(throwError(() => new Error(this.MAINTENANCE_ACTIVATED_ERROR)));
    }

    return await lastValueFrom(next.handle(request));
  }

  private isMaintenanceActive(currentDate: moment.Moment): boolean {
    if (this.maintenanceConfig?.enabled) {
      if (this.maintenanceConfig.from && this.maintenanceConfig.to) {
        const from = moment(this.maintenanceConfig.from);
        const to = moment(this.maintenanceConfig.to);
        return currentDate.isBetween(from, to, null, '[]');
      }
      return true; // if enabled but no dates, it's just enabled
    }
    return false;
  }

  private isBannerActive(currentDate: moment.Moment): boolean {
    if (this.maintenanceConfig?.banner?.enabled) {
        if (this.maintenanceConfig.from && this.maintenanceConfig.banner.displayBefore) {
            const from = moment(this.maintenanceConfig.from);
            const displayBefore = moment.duration(this.maintenanceConfig.banner.displayBefore);
            const bannerStartDate = moment(from).subtract(displayBefore);
            // The banner is active if current time is between the calculated banner start date and the maintenance start date.
            return currentDate.isBetween(bannerStartDate, from, null, '[)');
        }
        return true;
    }
    return false;
  }
}
